import { ResponsiveContainer, media } from 'app/assets/styles'
import { Loader } from 'app/common/loader'
import { SwitchPush } from 'app/common/switch-push'
import { CaptionText, H6, fonts } from 'app/common/typography'
import { CcCartCheck } from 'app/ecommerce/click-and-collect/cart-check'
import { Restaurant } from 'app/store-locator/restaurant'
import { push } from 'connected-react-router'
import React from 'react'
import type { IntlShape } from 'react-intl'
import { FormattedMessage, injectIntl } from 'react-intl'
import { useDispatch, useSelector } from 'react-redux'
import { colors as oldColors } from 'shared/assets/styles'
import type { RootState } from 'shared/store/reducers'
import type { Coordinates } from 'shared/store/store-locator/types'
import type { RestaurantShortDTO } from 'shared/types'
import { featureCodes } from 'shared/utils/const'
import { isEcommerceModeActive } from 'shared/utils/ecommerce'
import styled from 'styled-components'

const MAX_RESTAURANTS_DISPLAYED = 10
const { yellow } = oldColors
const { roboto } = fonts

type Props = {
    intl: IntlShape
}

type StoreProps = {
    userPosition: ?Coordinates,
    address: ?string,
    restaurants: ?Array<RestaurantShortDTO>,
    nearestRestaurants?: Array<RestaurantShortDTO>,
    visitedRestaurants: ?Array<RestaurantShortDTO>,
    loading: boolean,
    favRestaurant?: RestaurantShortDTO,
    authenticated: boolean,
    available: string[]
}

const _ListView = ({ intl }: Props) => {
    const dispatch = useDispatch()
    const {
        userPosition,
        address,
        restaurants,
        nearestRestaurants,
        visitedRestaurants,
        loading,
        favRestaurant,
        authenticated,
        available
    }: StoreProps = useSelector((state: RootState) => ({
        userPosition: state.storeLocatorPageState.userPosition,
        address: state.storeLocatorPageState.address,
        restaurants: state.storeLocatorPageState.restaurants,
        nearestRestaurants: state.storeLocatorPageState.nearestRestaurants,
        visitedRestaurants: state.storeLocatorPageState.visitedRestaurants,
        loading: state.storeLocatorPageState.loading,
        favRestaurant: state.kingdomProfilePageState.favRestaurant,
        authenticated: state.userState.authenticated,
        available: state.featuresState.available
    }))
    const idTitle =
        userPosition &&
        nearestRestaurants &&
        nearestRestaurants.length > 0 &&
        !address
            ? 'clickandcollect.search.restaurant.close'
            : favRestaurant
            ? 'kingdom.profile.form.restaurant.title'
            : visitedRestaurants
            ? 'clickandcollect.search.restaurant.last'
            : 'clickandcollect.search.restaurant.none'
    const listRestaurants =
        address && restaurants
            ? restaurants
            : userPosition && nearestRestaurants
            ? nearestRestaurants
            : favRestaurant
            ? [favRestaurant]
            : visitedRestaurants
    const listRestaurantsSliced =
        listRestaurants && listRestaurants.slice(0, MAX_RESTAURANTS_DISPLAYED)

    const isPaymentWithoutLoginEnabled = available.includes(
        featureCodes.WORKFLOW_ORDER_NOT_CONNECTED
    )

    const navigate = (to: string, state?: any) => {
        dispatch(push({ pathname: to, state }))
    }

    const renderRestaurantsListView = () => {
        return (
            <ScrollView>
                {listRestaurantsSliced &&
                    listRestaurantsSliced.map(
                        (item: RestaurantShortDTO, index) => {
                            const disabled = !isEcommerceModeActive(item)
                            return (
                                <CcCartCheck
                                    key={index}
                                    restaurant={item}
                                    disabled={disabled}>
                                    <Restaurant
                                        id={`restaurant-${item.id}`}
                                        restaurant={item}
                                        active={false}
                                        clickAndCollect={true}
                                    />
                                </CcCartCheck>
                            )
                        }
                    )}
            </ScrollView>
        )
    }

    const renderNotConnected = () => {
        const route = intl.formatMessage({
            id: 'routes.clickandcollect.signin.signup'
        })
        return (
            <ContainerPush>
                <SwitchPush
                    background="white"
                    btnColor={yellow}
                    title={intl.formatMessage({
                        id: 'clickandcollect.search.connect.title'
                    })}
                    baseline={intl.formatMessage({
                        id: 'clickandcollect.search.connect.description'
                    })}
                    upButton={intl.formatMessage({
                        id: 'clickandcollect.search.connect.button'
                    })}
                    bottomButton={intl.formatMessage({
                        id: 'clickandcollect.search.subscribe.button'
                    })}
                    onClickFirstButton={() => {
                        navigate(route, {
                            fromUrl: intl.formatMessage({
                                id: 'routes.clickandcollect.restaurant.choice'
                            })
                        })
                    }}
                    onClickSecondButton={() => {
                        navigate(route, {
                            signUp: true,
                            fromUrl: intl.formatMessage({
                                id: 'routes.clickandcollect.restaurant.choice'
                            })
                        })
                    }}
                    className="connect"
                />
            </ContainerPush>
        )
    }

    return (
        <div>
            <StyledLoader loading={loading}>
                {!Boolean(address) ? (
                    <TitleContainer>
                        <FormattedMessage id={idTitle}>
                            {(label: string) => <H6 font={roboto}>{label}</H6>}
                        </FormattedMessage>
                    </TitleContainer>
                ) : (
                    listRestaurantsSliced &&
                    !listRestaurantsSliced.length && (
                        <RestaurantsContainer>
                            <div>
                                <FormattedMessage
                                    id={
                                        'storelocator.search.results.none.title'
                                    }>
                                    {(label: string) => (
                                        <H6 font={roboto}>{label}</H6>
                                    )}
                                </FormattedMessage>
                                <FormattedMessage id="storelocator.search.results.none.baseline">
                                    {(text: string) => (
                                        <CaptionText>{text}</CaptionText>
                                    )}
                                </FormattedMessage>
                            </div>
                        </RestaurantsContainer>
                    )
                )}
                {listRestaurantsSliced && renderRestaurantsListView()}
            </StyledLoader>
            {!authenticated &&
                !isPaymentWithoutLoginEnabled &&
                renderNotConnected()}
        </div>
    )
}

const StyledLoader = styled(Loader)`
    display: flex;
    justify-content: center;
    min-height: 310px;
`

const TitleContainer = styled.div`
    padding-top: 40px;
    text-align: center;
`

const RestaurantsContainer = styled.div`
    padding: 28px 10px;
    min-height: 310px;
`

const ScrollView = styled(ResponsiveContainer)`
    overflow: hidden;
    width: 100%;
    margin: 0;
    ${media.medium`
        max-height: calc(100vh - 390px);
        overflow-y: scroll;
    `};
`

const ContainerPush = styled.div`
    display: flex;
    justify-content: center;
    margin-top: 20px;
    ${media.medium`
        margin-top: 50px;
    `};
`

export const ListView = injectIntl(_ListView)
